import { motion } from 'framer-motion'
import { User, Clock, PhoneCall, X } from 'lucide-react'
import { useChat } from '@/contexts/ChatContext'
import { Button } from './ui/Button'

export function EscalationBanner() {
  const { isEscalated, handoffPending, clearChat } = useChat()

  const handleEndSession = () => {
    if (window.confirm('Are you sure you want to end this session?')) {
      clearChat()
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="relative overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-red-500/10 to-orange-500/10" />
      
      <div className="relative px-4 py-3 border-b border-orange-200 dark:border-orange-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Status Icon */}
            <motion.div
              animate={{ 
                rotate: handoffPending ? 360 : 0,
                scale: handoffPending ? [1, 1.1, 1] : 1
              }}
              transition={{ 
                rotate: { duration: 2, repeat: handoffPending ? Infinity : 0 },
                scale: { duration: 1, repeat: handoffPending ? Infinity : 0 }
              }}
              className="flex-shrink-0"
            >
              {handoffPending ? (
                <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              ) : (
                <User className="w-5 h-5 text-green-600 dark:text-green-400" />
              )}
            </motion.div>

            {/* Status Text */}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 outdoor-contrast">
                {handoffPending ? 'Connecting to Human Agent...' : 'Connected to Human Agent'}
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                {handoffPending 
                  ? 'Please wait while we connect you to a human representative.'
                  : 'You are now chatting with a human agent who can provide personalized assistance.'
                }
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {/* Call Button (if escalated) */}
            {isEscalated && !handoffPending && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs font-medium text-green-700 border-green-300 hover:bg-green-50 dark:text-green-300 dark:border-green-600 dark:hover:bg-green-900/20"
              >
                <PhoneCall className="w-3 h-3 mr-1" />
                Call
              </Button>
            )}

            {/* End Session Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleEndSession}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              aria-label="End session"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Progress Bar for Handoff */}
        {handoffPending && (
          <div className="mt-3">
            <div className="h-1 bg-orange-200 dark:bg-orange-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-orange-500 dark:bg-orange-400"
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 3, ease: 'easeInOut' }}
              />
            </div>
            <div className="mt-1 text-xs text-orange-600 dark:text-orange-400">
              Estimated wait time: 1-2 minutes
            </div>
          </div>
        )}

        {/* Agent Info (if connected) */}
        {isEscalated && !handoffPending && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 flex items-center space-x-3 p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg backdrop-blur-sm"
          >
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Agent Sarah
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                <span>Online • Available to help</span>
              </div>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Festival Support Team
            </div>
          </motion.div>
        )}
      </div>

      {/* Animated border effect */}
      <motion.div
        className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-orange-400 to-red-400"
        initial={{ width: 0 }}
        animate={{ width: '100%' }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
    </motion.div>
  )
}